import { useState } from 'react';
import { FileText, Upload, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Input';
import { FileUpload } from '@/components/ui/FileUpload';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useDocuments } from '@/hooks/useDocuments';
import toast from 'react-hot-toast';

export function DocumentsPage() {
  const { documents, documentTypes, loading, upload, validate } = useDocuments();
  const [showUpload, setShowUpload] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  if (loading) return <LoadingSpinner />;

  const handleUpload = async () => {
    if (!selectedFile || !selectedTypeId) {
      toast.error('Selecciona un tipo de documento y un archivo');
      return;
    }
    try {
      setUploading(true);
      await upload(selectedFile, selectedTypeId);
      setShowUpload(false);
      setSelectedFile(null);
      setSelectedTypeId('');
      toast.success('Documento subido exitosamente');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async (docId: string) => {
    try {
      const result = await validate(docId);
      if (result.status === 'approved') {
        toast.success('Documento aprobado ✅');
      } else if (result.status === 'rejected') {
        toast.error(`Documento rechazado: ${result.rejection_reason || 'Calidad insuficiente'}`);
      } else {
        toast('Documento en revisión manual', { icon: '⏳' });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error de validación');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle size={18} className="text-green-500" />;
      case 'rejected': return <AlertCircle size={18} className="text-red-500" />;
      case 'pending': case 'validating': return <Clock size={18} className="text-yellow-500" />;
      default: return <FileText size={18} className="text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl tracking-wider">DOCUMENTOS</h1>
        <Button size="sm" onClick={() => setShowUpload(true)}>
          <Upload size={14} className="mr-1" /> Subir
        </Button>
      </div>

      {/* Required docs checklist */}
      <Card padding="lg">
        <h2 className="font-heading text-lg tracking-wider mb-3">REQUERIDOS</h2>
        <div className="space-y-2">
          {documentTypes.filter(dt => dt.is_required).map(dt => {
            const doc = documents.find(d =>
              d.document_type_id === dt.id &&
              (dt.applies_to === 'driver' ? !d.vehicle_id : d.vehicle_id)
            );
            return (
              <div key={dt.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                {doc ? getStatusIcon(doc.status) : <div className="w-[18px] h-[18px] rounded-full border-2 border-gray-300" />}
                <div className="flex-1">
                  <p className="text-sm font-medium">{dt.name}</p>
                  <p className="text-xs text-gray-400">{dt.applies_to === 'driver' ? 'Conductor' : 'Vehículo'}</p>
                </div>
                {doc ? (
                  <StatusBadge status={doc.status} />
                ) : (
                  <Button variant="outline" size="sm" onClick={() => { setSelectedTypeId(dt.id); setShowUpload(true); }}>
                    Subir
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Uploaded documents */}
      {documents.length > 0 && (
        <div>
          <h2 className="font-heading text-lg tracking-wider mb-3">MIS DOCUMENTOS</h2>
          <div className="space-y-2">
            {documents.map(doc => (
              <Card key={doc.id} className="flex items-center gap-3">
                {getStatusIcon(doc.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {doc.document_type?.name || doc.file_name || 'Documento'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    {doc.expiration_date && <span>Vence: {doc.expiration_date}</span>}
                    {doc.docval_confidence !== null && (
                      <span>Confianza: {doc.docval_confidence}%</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={doc.status} />
                  {doc.status === 'pending' && (
                    <Button variant="ghost" size="sm" onClick={() => handleValidate(doc.id)}>
                      Validar
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {documents.length === 0 && (
        <EmptyState
          title="Sin documentos"
          description="Sube tus documentos para completar tu perfil y desbloquear oportunidades."
          icon={<FileText size={28} className="text-gray-400" />}
          action={<Button onClick={() => setShowUpload(true)}>Subir documento</Button>}
        />
      )}

      {/* Upload modal */}
      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title="Subir Documento">
        <div className="space-y-4">
          <Select
            label="Tipo de documento"
            value={selectedTypeId}
            onChange={(e) => setSelectedTypeId(e.target.value)}
            options={documentTypes.map(dt => ({ value: dt.id, label: dt.name }))}
          />
          <FileUpload
            label="Archivo"
            onFileSelect={setSelectedFile}
          />
          <Button fullWidth onClick={handleUpload} loading={uploading} disabled={!selectedFile || !selectedTypeId}>
            Subir documento
          </Button>
        </div>
      </Modal>
    </div>
  );
}
